import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Divider, IconButton, List, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';

import { AuthStoreContext } from '../../Root';
import AdminNodeStore from '../../../stores/admin-node-store';
import { DbNode } from '../../../types/node';
import NodeView from '../../dashboard/node/NodeView';
import { nodeIcon, NodeChildrenSkeleton, NodeLoadError } from '../../dashboard/node/NodeItem';
import CreateNodeDialog from './CreateNodeDialog';
import EditNodeDialog from './EditNodeDialog';
import DeleteNodeDialog from './DeleteNodeDialog';

//=================================================================================================
interface AdminNodeItemProps {
    node: DbNode;
    depth: number;
    onNodeUpdated?: () => void;
}

const AdminNodeItem = observer(({ node, depth, onNodeUpdated }: AdminNodeItemProps) => {
    const [store] = useState(() => new AdminNodeStore(node.node_id));
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    useEffect(() => {
        store
            .load()
            .then(() => setLoaded(true))
            .catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    let content: React.ReactNode;
    if (store.loading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else if (loaded && store.items.length === 0) {
        content = undefined;
    } else {
        content = (
            <>
                {store.items.map((n) => (
                    <AdminNodeItem key={n.node_id} node={n} depth={depth + 1} onNodeUpdated={() => store.loadNode()} />
                ))}
            </>
        );
    }

    const actions = (
        <>
            <Tooltip title="Добавить дочерний узел">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setCreateOpen(true);
                    }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Редактировать">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditOpen(true);
                    }}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Удалить">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDeleteOpen(true);
                    }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    return (
        <>
            <NodeView
                name={node.name}
                description={node.description ?? undefined}
                icon={nodeIcon(node.type)}
                actions={actions}
                depth={depth}
            >
                {content}
            </NodeView>
            <CreateNodeDialog store={store} open={createOpen} onClose={() => setCreateOpen(false)} />
            <EditNodeDialog
                store={store}
                node={node}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={() => onNodeUpdated?.()}
            />
            <DeleteNodeDialog
                node={node}
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onDeleted={() => onNodeUpdated?.()}
            />
        </>
    );
});

//=================================================================================================
const NodeManager = observer(() => {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new AdminNodeStore(user?.node_id ?? 0));
    const [error, setError] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        store.load().catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    let content: React.ReactNode;
    if (store.loading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else {
        content = store.items.map((n) => (
            <AdminNodeItem key={n.node_id} node={n} depth={1} onNodeUpdated={() => store.loadNode()} />
        ));
    }

    return (
        <>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Управление структурой организации
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Иерархия подразделений, групп и скважин. Управляйте составом объектов и их вложенностью.
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        disableElevation
                        onClick={() => setCreateOpen(true)}
                    >
                        Добавить
                    </Button>
                </Box>
                <Divider />
                <List dense disablePadding sx={{ px: 1, py: 1 }}>
                    <NodeView name="Всё" icon={<LanguageIcon fontSize="small" />} depth={0} defaultOpen>
                        {content}
                    </NodeView>
                </List>
            </Paper>
            <CreateNodeDialog store={store} open={createOpen} onClose={() => setCreateOpen(false)} />
        </>
    );
});

export default NodeManager;
